import { cache } from "react"
import { i18nConfig } from "@/i18Config"
import type { IContentItem } from "@kontent-ai/delivery-sdk"

import {
  ChecklistSubscription,
  type Button as SDKButton,
} from "@/types/kontent-generated"
import { getDeliveryClient } from "@/lib/kontent/client"
import { getWebSpotlightApplicationTree } from "@/lib/kontent/config/getApplicationTree"
import { mapButtonToTButton } from "@/lib/kontent/mappers"
import { IChecklistSubscriptionProps } from "@/components/ui/molecules/checklist/subscription/ChecklistSubscription"
import { TButton } from "@/components/ui/atoms"

import { assertItemResponse, fetchKontent } from "../helpers"

/**
 * Maps an already-fetched SDK item, avoiding a redundant Delivery API call
 * when the item was already resolved by a parent query's depth parameter.
 */
export async function getChecklistSubscriptionFromItem(
  rawItem: IContentItem,
  previewMode = false
): Promise<IChecklistSubscriptionProps> {
  const item = rawItem as ChecklistSubscription

  const buttonItem = item.elements.button.linkedItems[0] as
    | SDKButton
    | undefined
  let button: TButton | undefined
  if (buttonItem) {
    const { byCodename } = await getWebSpotlightApplicationTree(previewMode)
    button = mapButtonToTButton(buttonItem, byCodename)
  }

  return {
    title: item.elements.title.value,
    iframe: item.elements.idfuse_iframe_url.value,
    description: item.elements.description.value,
    button: button,
  }
}

async function _fetchChecklistSubscription(
  codename: string,
  previewMode = false
): Promise<IChecklistSubscriptionProps> {
  return fetchKontent("Checklist subscription", async () => {
    const client = getDeliveryClient(previewMode)
    const response = await client
      .item<ChecklistSubscription>(codename)
      .languageParameter(i18nConfig.defaultLocale)
      .depthParameter(2)
      .toPromise()

    const item = assertItemResponse(response, "Checklist subscription")
    return getChecklistSubscriptionFromItem(item, previewMode)
  })
}

export const getChecklistSubscription = cache(_fetchChecklistSubscription)
